import { useRef, useSyncExternalStore } from 'react'
import type { Scheme } from './theme'
import { adminGate } from './adminGate.ts'

/**
 * Five tabs, named after what Doug does rather than what the table is
 * called (B3) — `home`/`feed`/`venues` became `today`/`browse`/`places`, and
 * WP 6.3 folded `places` into `browse` itself: an Events/Places segmented
 * control at the top of the one screen, not a fifth icon in the bar. Saved
 * was the same move a package earlier — it is just another kind of list, so
 * it belongs on the Lists screen with the others rather than holding a tab
 * of its own.
 *
 * A saved event is still an `entry_state` row and not a `list_item`: that
 * table is the taste signal store and feeds scoring. The merge is what Doug
 * sees, not where the rows live.
 *
 * WP 6.4 put `feed` back in the slot `places` gave up — a card swiped one at
 * a time. WP 9.2 gives that slot to `butler`, the Ask tab: the Feed became a
 * conversation (`docs/butler-plan/README.md`), and `#/feed` is an alias for
 * it below, so a bookmark lands on the thing that replaced it.
 */
export const TABS = ['today', 'browse', 'butler', 'lists', 'year'] as const
export type Tab = (typeof TABS)[number]

/**
 * Routes that are reachable by hash and are not tabs.
 *
 * `dev` is the component specimen at `#/dev/ui`: every primitive in every
 * state, for a design pass to look at. It is deliberately not in `TABS`,
 * because `TABS` is what the bottom bar is built from — adding it there would
 * put a sixth icon in front of Doug, and adding it to a second list that the
 * bar also reads would be the same value answering two questions.
 *
 * `city` (WP 3.1) is the citywide news list at `#/city`, where Today's three
 * lines send you for the rest. Here for the same reason `dev` is: it is a
 * destination rather than a place you live, and N1's "nothing else that
 * pings" is not served by a sixth icon for something read once a week.
 */
export const EXTRA_ROUTES = ['dev', 'city'] as const

/** Anywhere the hash can point: the five tabs and the routes beside them. */
export type Screen = Tab | (typeof EXTRA_ROUTES)[number]

const SCREENS: readonly string[] = [...TABS, ...EXTRA_ROUTES]

/** Routes only an admin may open (U0.3): the component specimen. */
export const ADMIN_SCREENS: readonly string[] = ['dev']

/** The screen to draw for a route, once it is known who is asking. A non-admin
 *  — and a user whose `app_admins` read has not answered, `undefined` — gets
 *  Today for an admin screen; the hash is left alone, so Doug's reload on
 *  `#/dev/ui` lands there as soon as the answer is in. Pure, for the suite. */
export function gateScreen(tab: Screen, isAdmin: boolean | undefined): Screen {
  return ADMIN_SCREENS.includes(tab) && adminGate(isAdmin) === 'hide' ? 'today' : tab
}

/** Old tab names, kept working after the B3 rename so a bookmark or a link
 *  someone still has does not land on nothing (a rule this brief tests).
 *
 *  Null-prototype on purpose. A plain object literal answers `ALIASES[head]`
 *  out of `Object.prototype` for `constructor`, `toString`, `valueOf` and
 *  `__proto__`, so `#/constructor` resolved to a *function* instead of
 *  falling through to the unknown-head fallback — and since that value
 *  matches no tab, every pane stayed hidden and the app rendered blank.
 *  Verified against the built app before the fix: zero visible panes.
 *
 *  Same shape as the bug this codebase keeps writing. `ALIASES[head]` was
 *  doing two jobs — deciding *whether* `head` is an alias and saying *what*
 *  it maps to — and the prototype chain answers the first one wrong.
 *
 *  `feed` left this table in WP 6.4, when it became a tab of its own (an
 *  alias is checked before `SCREENS` below, so it would have shadowed the
 *  real screen). WP 9.2 retired that screen and `feed` is back, now
 *  pointing at `butler` — the Ask tab took the Feed's slot, and a `#/feed`
 *  bookmark should land on what replaced it rather than fall through to
 *  Today. No shadowing risk this time: `feed` is no longer in `TABS`. */
const ALIASES: Record<string, Tab> = Object.assign(Object.create(null), {
  home: 'today',
  feed: 'butler',
})

/**
 * WP 6.3: `places` stopped being a tab, so a bare `#/places…` needs
 * somewhere to land too, and so does `venues` — the older name `ALIASES`
 * used to carry on its own, before it also had to start carrying a query
 * key. Kept as its own set rather than folded into `ALIASES`,
 * because the two answer different shapes of question: `ALIASES` maps a
 * head to *one* tab; a place head maps to a tab *and* a query key together
 * (`?mode=places`), which `parseHash` below has to write in as well as the
 * tab — one alias table cannot answer both without a value doing two jobs,
 * which is the trap CLAUDE.md's list opens with. A `Set` rather than
 * `ALIASES`'s object shape, since there is no second value here for an
 * object literal to answer wrong — `has()` alone is the whole question. */
const PLACE_HEADS = new Set(['places', 'venues'])

/**
 * A tab, and optionally the thing inside it the route is pointing at.
 *
 * Doug, 21 September: "When i click on the list from the homepage it should
 * open that list not the list tab." Home's list peeks set the hash to
 * `/lists`, and `Lists` opens on its index because that is all the route
 * carried.
 *
 * The failure was worse than it looked. `parse` tested `TABS.includes(h)`
 * against the whole hash, so the obvious fix — setting `#/lists/<id>` from
 * Home — would have matched nothing and **silently landed on Home**, which is
 * the shape of bug this codebase keeps paying for: one value answering two
 * questions. So the hash is split first and the tab is matched on its own
 * segment.
 */
export interface Route {
  /** The first segment. A `Screen` rather than a `Tab` since WP 2.2: `#/dev/ui`
   *  is a route the bar does not carry. */
  tab: Screen
  /** The second path segment, where there is one. `Lists` reads it as a list
   *  id; Browse reads it as a deep-link type (`#/browse/concert`) or as the
   *  literal `hidden` (`#/browse/hidden`). Never a partial tab name — an
   *  unknown tab falls back to Home and drops the rest with it, because a
   *  detail id belonging to no screen is not a route. */
  id: string | null
  /**
   * WP 3.2: everything after a `?` inside the hash — `#/browse?when=week`,
   * not the page's own `?search`, which `parseThemeParam` reads separately
   * and for a different reason (a one-shot override applied before the hash
   * is even parsed). Browse reads `when`/`kind`/`q`/`day` (WP 3.2),
   * `person`/`ensemble` (E5) and `series` (nightlife N7, a party's page —
   * `#/browse?series=koffaein`), and the place page reads `tab` (WP 3.4a);
   * every other screen gets the empty object. It stays a plain `Record` rather than a union of those keys
   * because a route has no business knowing what any one screen's query
   * keys mean — which is also why two packages could add to it in parallel
   * without the type needing to know about either.
   */
  query: Record<string, string>
}

/**
 * The hash, as a route. Pure and exported so it can be tested from the
 * Worker's suite, which is the only suite `npm run check` gates on — the same
 * reason `filmRank` and `yearStats` are separate files.
 */
export function parseHash(hash: string): Route {
  // The query lives inside the hash — `#/browse?when=week` — because the
  // hash is the whole of this app's routing; splitting it off first means
  // '?' can never end up inside `id` no matter which segment it was typed
  // after (`#/browse?when=week` and the hypothetical `#/browse/concert?when=`
  // both work the same way).
  const [path, queryString] = hash.replace(/^#\/?/, '').split('?')
  const [head, id] = path.split('/')
  // An id that does not decode is not an id. `decodeURIComponent` throws on a
  // lone '%', which a hand-edited URL produces easily and which would
  // otherwise take the whole app down on a hashchange.
  let decoded: string | null = null
  if (id) {
    try { decoded = decodeURIComponent(id) } catch { decoded = id }
  }
  const query: Record<string, string> = {}
  if (queryString) {
    for (const [k, v] of new URLSearchParams(queryString)) query[k] = v
  }
  // WP 6.3: a place head lands on Browse, in Places mode, with the id (a
  // venue) carried exactly as a kind deep-link already is — checked ahead of
  // `ALIASES`/`SCREENS` below because `places` is no longer a `Screen` those
  // two can resolve on their own.
  if (PLACE_HEADS.has(head)) return { tab: 'browse', id: decoded, query: { ...query, mode: 'places' } }
  const tab = ALIASES[head] ?? (SCREENS.includes(head) ? (head as Screen) : null)
  if (!tab) return { tab: 'today', id: null, query: {} }
  return { tab, id: decoded, query }
}

/**
 * `?theme=dark` or `?theme=light`, read off the whole URL.
 *
 * A one-shot override, applied by `main.tsx` before the first render and never
 * stored: it is how the UI-audit harness takes a dark screenshot of a screen
 * whose preference lives on the device, and a screenshot run must not change
 * what Doug's phone remembers.
 *
 * Matched against the raw URL rather than parsed out of `location.search`,
 * because the harness builds its targets by appending `/#/<tab>` to a base URL
 * — `…?theme=dark/#/home` — so the value can be followed by a path separator
 * as easily as by `&` or the end of the string. Pure, and exported for the same
 * reason `parseHash` is.
 */
export function parseThemeParam(url: string): Scheme | null {
  const m = /[?&]theme=(dark|light)\b/.exec(url)
  return m ? (m[1] as Scheme) : null
}

/** One parse per distinct hash, so `useSyncExternalStore`'s snapshot is
 *  reference-stable between renders that have not seen a change — without
 *  this, `getSnapshot` handing back a fresh object on every call reads to
 *  React as the store changing on every render, which is the infinite-loop
 *  footgun `useSyncExternalStore`'s own docs warn about. */
let cache: { hash: string; route: Route } | null = null

function getSnapshot(): Route {
  const hash = window.location.hash
  if (cache && cache.hash === hash) return cache.route
  const route = parseHash(hash)
  cache = { hash, route }
  return route
}

/** Every mounted `useRoute()`'s React-supplied callback, so `go` can call them
 *  straight away instead of only setting the hash and waiting on the
 *  browser's own `hashchange` — which fires as a later task, not inside the
 *  script that set it. */
const listeners = new Set<() => void>()

function subscribe(onStoreChange: () => void): () => void {
  window.addEventListener('hashchange', onStoreChange)
  listeners.add(onStoreChange)
  return () => {
    window.removeEventListener('hashchange', onStoreChange)
    listeners.delete(onStoreChange)
  }
}

/** Hash routing rather than component state, so the phone's back gesture moves
 *  between tabs instead of leaving the installed app.
 *
 *  It now moves *out of an open list* first, for free: `#/lists/<id>` and
 *  `#/lists` are two history entries, so back closes the list before it leaves
 *  the tab — which is what the gesture means on a phone.
 *
 *  WP 3.2 split this from a single `[route, go]` pair into a read
 *  (`useRoute`) and a write (`go`, below): Browse needed a `go` it could call
 *  from outside the component holding the route — a `Segmented` `onChange`, a
 *  debounced search box — and `useSyncExternalStore` is what lets more than
 *  one mounted `useRoute()` agree on the same hash without each holding its
 *  own copy that could drift from the URL a moment after a sibling wrote it. */
export function useRoute(): Route {
  return useSyncExternalStore(subscribe, getSnapshot)
}

/** Whether the route is on `tab` — a boolean, so a screen that reads it
 *  re-renders when it is shown or hidden and on no other route change, which
 *  is what `useRoute()` itself would cost it (a4-13). */
export function useOnTab(tab: Screen): boolean {
  return useSyncExternalStore(subscribe, () => getSnapshot().tab === tab)
}

/**
 * `value` while the screen is shown; while it is hidden, the last value it
 * was shown with (a4-13).
 *
 * Every tab stays mounted (X7), so a save on Browse re-ran Today's whole
 * derivation of the feed behind its `hidden` div, and a feed refetch re-ran
 * all five. Handing a hidden screen's memos the value they already have means
 * they hit, and the work happens once, when the screen is next shown — on the
 * tab switch, for the one screen that is about to be seen.
 *
 * The first value always gets through: a screen mounted hidden on a cold load
 * still derives from the data when it arrives, rather than holding the
 * `undefined` it mounted with until someone opens it.
 */
export function useHeldWhileHidden<T>(value: T, shown: boolean): T {
  const held = useRef(value)
  if (shown || held.current === undefined) held.current = value
  return held.current
}

/**
 * Navigate. `id` is written when truthy and dropped otherwise — `undefined`
 * and `null` do the same thing, so `go('browse', undefined, { … })` leaves
 * `#/browse/hidden` for `#/browse`, it does not preserve the id. Every
 * caller passes it explicitly for that reason; if a "narrow in place" is
 * ever wanted, it has to read the current id and pass it back.
 */
export function go(tab: Tab, id?: string | null, query?: Record<string, string>): void {
  window.location.hash = hashFor(tab, id, query)
  // Notify synchronously rather than waiting on the real `hashchange` event
  // (which still fires and finds the same value cached above — harmless, no
  // extra render). D6's view transition wraps a tab switch in `flushSync`,
  // and that only has something to capture if the state change happens
  // inside the callback rather than on a later task.
  for (const l of listeners) l()
}

/**
 * A venue name's destination (a1-28): the place page, `#/places/<id>` in the
 * router's own spelling — `#/browse/<id>?mode=places`, which the phone draws
 * as the place page and the desk as Browse narrowed to that building.
 */
export function goPlace(id: string): void {
  go('browse', id, { mode: 'places' })
}

/**
 * Where a tap on the tab bar goes (a1-32, Doug's choice): every tab returns
 * as you left it, and tapping the tab you are already on goes to its index.
 *
 * The hash stays the one source of truth: what is remembered is the last
 * hash each tab was on — Browse's `?kind=film&q=bach`, the list open in
 * `#/lists/<id>` — and returning is navigating to it, so Back, a reload and
 * a link all mean what they meant. A remembered hash that no longer parses
 * to its own tab (an alias that has moved, a hand-edited one) is not used.
 *
 * `reset` says the tap was on the active tab: the caller goes to the bare
 * tab, scrolls to the top, and lets the screen drop state the hash does not
 * hold (Browse's Filters sheet). Pure and exported for the root suite.
 */
export function tabTarget(
  next: Tab, current: Screen, remembered: Partial<Record<Tab, string>>,
): { hash: string; reset: boolean } {
  if (next === current) return { hash: `#/${next}`, reset: true }
  const raw = remembered[next]
  const kept = raw && (raw.startsWith('#') ? raw : `#${raw}`)
  if (kept && parseHash(kept).tab === next) return { hash: kept, reset: false }
  return { hash: `#/${next}`, reset: false }
}

/** `go`, for a whole hash already written — `tabTarget`'s answer. */
export function goHash(hash: string): void {
  window.location.hash = hash.replace(/^#/, '')
  for (const l of listeners) l()
}

/** The event a re-tap on the active tab raises (`tabTarget`'s `reset`), for
 *  a screen holding state the hash does not: `detail` is the tab. */
export const TAB_RESET = 'kiez:tab-reset'

function hashFor(tab: Tab, id?: string | null, query?: Record<string, string>): string {
  const qs = query && Object.keys(query).length ? `?${new URLSearchParams(query)}` : ''
  return id ? `/${tab}/${encodeURIComponent(id)}${qs}` : `/${tab}${qs}`
}

/**
 * `go`, without a new history entry: the current one is rewritten in place.
 *
 * For a URL that is a *correction* of the one Doug navigated to rather than a
 * second place — `#/browse/film` normalised to `#/browse?kind=film` (a1-03).
 * Pushing that left two entries for one screen, and Back from the second
 * landed on the first, which normalised forward again: Back was trapped.
 *
 * `replaceState` fires neither `popstate` nor `hashchange`, so this notifies
 * the mounted `useRoute()`s itself, the way `go` does. That silence is also
 * what lets it write a URL under an open sheet without closing it: assigning
 * `location.hash` there fires `popstate`, which `ui/Sheet.tsx` reads as Back.
 * The state is written as `null` on purpose — when the entry being replaced
 * is an open sheet's own (`{ uSheet }`), the sheet then sees on close that
 * its entry has become a real navigation and leaves it in history rather
 * than popping it (`Sheet.tsx`'s `history.state` check).
 */
export function replace(tab: Tab, id?: string | null, query?: Record<string, string>): void {
  history.replaceState(null, '', `#${hashFor(tab, id, query)}`)
  for (const l of listeners) l()
}
